import "./columnResize.css";
import { persistFromDirective } from "../columnSettings/columnSettings";

var DEFAULT_MIN_WIDTH = 80;

function parseOptions(binding) {
  var value = binding && binding.value;
  if (!value) {
    return { headers: null, visibleHeaders: null, minWidth: DEFAULT_MIN_WIDTH };
  }
  if (Array.isArray(value)) {
    return {
      headers: value,
      visibleHeaders: value,
      minWidth: DEFAULT_MIN_WIDTH,
    };
  }
  return {
    headers: value.headers || null,
    visibleHeaders: value.visibleHeaders || value.headers || null,
    minWidth:
      value.minWidth != null && value.minWidth !== ""
        ? Number(value.minWidth)
        : DEFAULT_MIN_WIDTH,
  };
}

function isRtl(el) {
  var root =
    (el && el.closest && el.closest(".v-application")) ||
    document.body ||
    document.documentElement;
  var dir =
    (root && root.getAttribute && root.getAttribute("dir")) ||
    document.documentElement.getAttribute("dir") ||
    getComputedStyle(root || document.documentElement).direction;
  return dir === "rtl";
}

function getTable(el) {
  if (!el) return null;
  if (el.tagName === "TABLE") return el;
  return el.querySelector("table");
}

function resolveHeader(options, index) {
  var visible = options.visibleHeaders;
  var all = options.headers;
  var current = Array.isArray(visible) ? visible[index] : null;
  if (!current) return null;
  if (Array.isArray(all) && current.value != null) {
    var matched = all.find(function (header) {
      return header && header.value === current.value;
    });
    return matched || current;
  }
  return current;
}

function resolveMinWidth(options, index) {
  var header = resolveHeader(options, index);
  if (header && header.minWidth != null && header.minWidth !== "") {
    return Number(header.minWidth);
  }
  return options.minWidth != null ? Number(options.minWidth) : DEFAULT_MIN_WIDTH;
}

function clearHandles(table) {
  if (!table) return;
  table.querySelectorAll(".column-resize-handle").forEach(function (handle) {
    handle.remove();
  });
  table.querySelectorAll(".column-resize-th").forEach(function (th) {
    th.classList.remove("column-resize-th");
  });
}

function applyColumnWidth(table, index, width) {
  var ths = table.querySelectorAll("thead tr > *");
  var th = ths[index];
  if (th) {
    th.style.width = width + "px";
    th.style.minWidth = width + "px";
    th.style.maxWidth = width + "px";
  }

  table.querySelectorAll("tbody tr").forEach(function (row) {
    var cell = row.children[index];
    if (!cell) return;
    cell.style.width = width + "px";
    cell.style.minWidth = width + "px";
    cell.style.maxWidth = width + "px";
  });
}

function setMinState(handle, isMin) {
  if (!handle) return;
  if (isMin) {
    handle.classList.add("is-min");
  } else {
    handle.classList.remove("is-min");
  }
}

function onHandleMouseDown(event, context) {
  event.preventDefault();
  event.stopPropagation();

  var el = context.el;
  var binding = context.binding;
  var index = context.index;
  var th = context.th;
  var table = getTable(el);
  if (!table) return;

  var options = parseOptions(context.el.__columnResizeBinding || binding);
  var startX = event.clientX;
  var startWidth = th.offsetWidth;
  var rtl = isRtl(el);
  var handle = event.currentTarget;
  var minWidth = resolveMinWidth(options, index);

  handle.classList.add("is-active");
  document.body.classList.add("column-resize--dragging");
  setMinState(handle, startWidth <= minWidth);

  function onMouseMove(moveEvent) {
    var delta = moveEvent.clientX - startX;
    var rawWidth = startWidth + (rtl ? -delta : delta);
    var atMin = rawWidth <= minWidth;
    var nextWidth = atMin ? minWidth : rawWidth;

    applyColumnWidth(table, index, nextWidth);
    setMinState(handle, atMin);

    var header = resolveHeader(options, index);
    if (header) {
      header.width = nextWidth;
    }
  }

  function onMouseUp() {
    handle.classList.remove("is-active");
    setMinState(handle, false);
    document.body.classList.remove("column-resize--dragging");
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    persistFromDirective(
      el.__columnResizeVnode,
      el.__columnResizeBinding || binding,
      options.headers
    );
  }

  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
}

function createHandle(el, binding, index, th) {
  var handle = document.createElement("div");
  handle.className = "column-resize-handle";
  handle.title = "";

  var icon = document.createElement("i");
  icon.className = "column-resize-min-icon mdi mdi-lock-outline";
  icon.setAttribute("aria-hidden", "true");
  handle.appendChild(icon);

  handle.addEventListener("mousedown", function (event) {
    onHandleMouseDown(event, {
      el: el,
      binding: el.__columnResizeBinding || binding,
      index: index,
      th: th,
    });
  });

  return handle;
}

function bindHandles(el, binding) {
  if (document.body.classList.contains("column-resize--dragging")) {
    return;
  }

  var table = getTable(el);
  if (!table) return;

  var headerCells = table.querySelectorAll("thead tr > *");
  var count = headerCells.length;
  if (!count) return;

  if (
    el.__columnResizeBound &&
    el.__columnResizeCount === count &&
    table.querySelector(".column-resize-handle")
  ) {
    el.__columnResizeBinding = binding;
    return;
  }

  clearHandles(table);

  headerCells.forEach(function (th, index) {
    th.classList.add("column-resize-th");
    if (getComputedStyle(th).position === "static") {
      th.style.position = "relative";
    }
    th.appendChild(createHandle(el, binding, index, th));
  });

  el.__columnResizeBound = true;
  el.__columnResizeCount = count;
  el.__columnResizeBinding = binding;
}

function scheduleBind(el, binding, vnode) {
  el.__columnResizeBinding = binding;
  el.__columnResizeVnode = vnode;
  if (el.__columnResizeTimer) {
    clearTimeout(el.__columnResizeTimer);
  }
  el.__columnResizeTimer = setTimeout(function () {
    bindHandles(el, binding);
  }, 0);
}

var ColumnResizeDirective = {
  inserted: function (el, binding, vnode) {
    scheduleBind(el, binding, vnode);
  },
  componentUpdated: function (el, binding, vnode) {
    scheduleBind(el, binding, vnode);
  },
  unbind: function (el) {
    if (el.__columnResizeTimer) {
      clearTimeout(el.__columnResizeTimer);
      el.__columnResizeTimer = null;
    }
    clearHandles(getTable(el));
    document.body.classList.remove("column-resize--dragging");
  },
};

export default {
  install: function (Vue) {
    Vue.directive("column-resize", ColumnResizeDirective);
  },
};
